import { Component } from "@theme/component";

/**
 * A custom element that handles blog post filtering by tags and client-side pagination.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [filterContainer] - The filter container element.
 * @property {HTMLElement} [filterTabs] - The desktop filter tabs container.
 * @property {HTMLButtonElement[]} [filterTab] - The filter tab buttons.
 * @property {HTMLElement} [dropdownContainer] - The mobile dropdown container.
 * @property {HTMLButtonElement} [dropdownTrigger] - The dropdown trigger button.
 * @property {HTMLElement} [dropdownText] - The dropdown selected text display.
 * @property {HTMLElement} [dropdownMenu] - The dropdown menu.
 * @property {HTMLElement[]} [dropdownItem] - The dropdown menu items.
 * @property {HTMLElement} [grid] - The grid container for blog posts.
 * @property {HTMLElement[]} [cards] - The blog post card elements.
 * @property {HTMLElement} [pagination] - The pagination container.
 * @property {HTMLElement} [paginationPages] - The pagination pages container.
 * @property {HTMLButtonElement} [paginationNext] - The next button.
 *
 * @extends Component<Refs>
 */
export default class BlogPostsFilter extends Component {
  /** @type {string} */
  #currentTag = "";

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #itemsPerPage = 6;

  /** @type {HTMLElement[]} */
  #filteredCards = [];

  /** @type {(event: MouseEvent) => void} */
  #boundHandleClickOutside;

  connectedCallback() {
    super.connectedCallback();

    // Get items per page from attribute
    const itemsPerPage = this.getAttribute("items-per-page");
    if (itemsPerPage) {
      this.#itemsPerPage = parseInt(itemsPerPage, 10);
    }

    // Bind click outside handler
    this.#boundHandleClickOutside = this.#handleClickOutside.bind(this);
    document.addEventListener("click", this.#boundHandleClickOutside);

    // Initialize
    this.#filteredCards = this.#getAllCards();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener("click", this.#boundHandleClickOutside);
  }

  /**
   * Get all card elements
   * @returns {HTMLElement[]}
   */
  #getAllCards() {
    return Array.isArray(this.refs.cards) ? this.refs.cards : [];
  }

  /**
   * Filter cards by tag
   * @param {string} tag - The tag to filter by (empty string for all)
   * @returns {HTMLElement[]}
   */
  #filterCardsByTag(tag) {
    const allCards = this.#getAllCards();

    if (!tag) {
      return allCards;
    }

    return allCards.filter((card) => {
      const cardTags = card.dataset.tags?.split(",") || [];
      return cardTags.includes(tag);
    });
  }

  /**
   * Get the total number of pages
   * @returns {number}
   */
  #getTotalPages() {
    return Math.ceil(this.#filteredCards.length / this.#itemsPerPage);
  }

  /**
   * Update visibility of cards based on current filter and page
   */
  #updateCardVisibility() {
    const allCards = this.#getAllCards();
    const startIndex = (this.#currentPage - 1) * this.#itemsPerPage;
    const endIndex = startIndex + this.#itemsPerPage;

    // Hide all cards first
    allCards.forEach((card) => {
      card.hidden = true;
    });

    // Show only filtered cards for current page
    this.#filteredCards.slice(startIndex, endIndex).forEach((card) => {
      card.hidden = false;
    });
  }

  /**
   * Update pagination UI
   */
  #updatePagination() {
    const { pagination, paginationPages, paginationNext } = this.refs;
    const totalPages = this.#getTotalPages();

    if (!pagination || !paginationPages || !paginationNext) return;

    // Hide pagination if only one page or no items
    if (totalPages <= 1) {
      pagination.hidden = true;
      return;
    }

    pagination.hidden = false;

    // Clear existing page buttons
    paginationPages.innerHTML = "";

    // Create page buttons
    for (let i = 1; i <= totalPages; i++) {
      const pageButton = document.createElement("button");
      pageButton.className = "blog-pagination__page";
      if (i === this.#currentPage) {
        pageButton.classList.add("blog-pagination__page--active");
      }
      pageButton.textContent = String(i);
      pageButton.setAttribute("aria-label", `Page ${i}`);
      pageButton.setAttribute(
        "aria-current",
        i === this.#currentPage ? "page" : "false"
      );
      pageButton.addEventListener("click", () => this.#goToPage(i));
      paginationPages.appendChild(pageButton);
    }

    // Update next button state
    paginationNext.disabled = this.#currentPage >= totalPages;
  }

  /**
   * Update filter tabs UI (desktop)
   */
  #updateFilterTabs() {
    const { filterTab } = this.refs;

    if (!Array.isArray(filterTab)) return;

    filterTab.forEach((tab) => {
      const tabTag = tab.dataset.tag || "";
      const isActive = tabTag === this.#currentTag;

      tab.classList.toggle("blog-filter__tab--active", isActive);
      tab.setAttribute("aria-pressed", String(isActive));
    });
  }

  /**
   * Update filter dropdown (mobile)
   */
  #updateFilterDropdown() {
    const { dropdownText, dropdownItem, dropdownContainer, dropdownTrigger } =
      this.refs;

    if (!dropdownText || !Array.isArray(dropdownItem)) return;

    // Update dropdown items active state
    dropdownItem.forEach((item) => {
      const itemValue = item.dataset.value || "";
      const isActive = itemValue === this.#currentTag;

      item.classList.toggle("blog-filter__dropdown-item--active", isActive);
      item.setAttribute("aria-selected", String(isActive));

      // Update display text when active
      if (isActive) {
        dropdownText.textContent = item.textContent?.trim() || "";
      }
    });

    // Close dropdown after selection
    this.#closeDropdown();
  }

  /**
   * Close the dropdown
   */
  #closeDropdown() {
    const { dropdownContainer, dropdownTrigger } = this.refs;
    if (dropdownContainer) {
      dropdownContainer.dataset.open = "false";
    }
    if (dropdownTrigger) {
      dropdownTrigger.setAttribute("aria-expanded", "false");
    }
  }

  /**
   * Handle click outside to close dropdown
   * @param {MouseEvent} event
   */
  #handleClickOutside(event) {
    const { dropdownContainer } = this.refs;
    if (!dropdownContainer) return;

    if (!dropdownContainer.contains(/** @type {Node} */ (event.target))) {
      this.#closeDropdown();
    }
  }

  /**
   * Render the current state
   */
  #render() {
    this.#updateCardVisibility();
    this.#updatePagination();
    this.#updateFilterTabs();
    this.#updateFilterDropdown();
  }

  /**
   * Go to a specific page
   * @param {number} page
   */
  #goToPage(page) {
    const totalPages = this.#getTotalPages();
    if (page < 1 || page > totalPages) return;

    this.#currentPage = page;
    this.#render();

    // Scroll to top of grid
    const { grid } = this.refs;
    if (grid) {
      grid.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  /**
   * Set filter tag
   * @param {string} tag
   */
  #setFilter(tag) {
    this.#currentTag = tag;
    this.#filteredCards = this.#filterCardsByTag(tag);
    this.#currentPage = 1; // Reset to first page when filter changes
    this.#render();
  }

  /**
   * Handle filter tab click (desktop)
   * @param {MouseEvent} event
   */
  handleFilterTabClick(event) {
    const target =
      event.target instanceof Element
        ? event.target.closest("button[data-tag]")
        : null;
    if (!target) return;

    const tag = target.dataset.tag || "";
    this.#setFilter(tag);
  }

  /**
   * Handle dropdown toggle (mobile)
   * @param {MouseEvent} event
   */
  handleDropdownToggle(event) {
    event.stopPropagation();
    const { dropdownContainer, dropdownTrigger } = this.refs;

    if (!dropdownContainer || !dropdownTrigger) return;

    const isOpen = dropdownContainer.dataset.open === "true";
    dropdownContainer.dataset.open = String(!isOpen);
    dropdownTrigger.setAttribute("aria-expanded", String(!isOpen));
  }

  /**
   * Handle dropdown item click (mobile)
   * @param {MouseEvent} event
   */
  handleDropdownItemClick(event) {
    const target =
      event.target instanceof Element
        ? event.target.closest("[data-value]")
        : null;
    if (!target) return;

    const value = /** @type {HTMLElement} */ (target).dataset.value || "";
    this.#setFilter(value);
  }

  /**
   * Handle next button click
   */
  handleNextClick() {
    this.#goToPage(this.#currentPage + 1);
  }
}

if (!customElements.get("blog-posts-filter")) {
  customElements.define("blog-posts-filter", BlogPostsFilter);
}
